import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { config } from "../config";
import { ChannelBanner, loadChannelBanner } from "./channelBanner";
import { isHlsSource, loadPlaylist, PlaylistItem, PlaylistManifest } from "./channelPlaylist";

type Status = "loading" | "live" | "playlist" | "offline" | "error";

export default function ViewerPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const statusRef = useRef<Status>("loading");
  const liveRetryTimerRef = useRef<number | null>(null);
  const liveMonitorTimerRef = useRef<number | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [headline, setHeadline] = useState("Connecting...");
  const [playlistSize, setPlaylistSize] = useState(0);
  const [banner, setBanner] = useState<ChannelBanner | null>(null);

  useEffect(() => {
    let disposed = false;
    const refreshBanner = () => {
      void loadChannelBanner().then((next) => {
        if (!disposed) setBanner(next);
      });
    };
    refreshBanner();
    const timer = window.setInterval(refreshBanner, 60000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    const video = videoEl;

    let disposed = false;
    let playlistManifest: PlaylistManifest = { items: [] };
    let liveHls: Hls | null = null;
    let playbackHls: Hls | null = null;
    let playlistAbort = false;
    let livePollTimer: number | null = null;
    let modeCleanup: (() => void) | null = null;
    let liveBootTimer: number | null = null;
    const LIVE_BOOT_RETRY_MS = 1200;
    const LIVE_BOOT_TIMEOUT_MS = 12000;
    const LIVE_MONITOR_MS = 5000;
    const LIVE_STALL_GRACE_MS = 45000;
    const LIVE_OFFLINE_GRACE_MS = 180000;
    let lastLiveTime = 0;
    let lastLiveProgressAt = Date.now();
    let liveWentOfflineAt = 0;
    let currentLiveSource = "";
    let stickyLiveSource = "";

    const fetchJsonWithTimeout = async <T,>(url: string, timeoutMs: number): Promise<T | null> => {
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timeout = window.setTimeout(() => controller?.abort(), timeoutMs);
      try {
        const res = await fetch(url, {
          cache: "no-store",
          signal: controller?.signal,
        });
        if (!res.ok) return null;
        return await res.json() as T;
      } catch {
        return null;
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const fetchTextWithTimeout = async (url: string, timeoutMs: number): Promise<string> => {
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      const timeout = window.setTimeout(() => controller?.abort(), timeoutMs);
      try {
        const res = await fetch(url, {
          cache: "no-store",
          signal: controller?.signal,
        });
        if (!res.ok) return "";
        return await res.text();
      } catch {
        return "";
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const hlsLooksLive = async (url: string): Promise<boolean> => {
      const text = await fetchTextWithTimeout(url, 3500);
      if (!text || text.includes("#EXT-X-ENDLIST")) return false;

      const programDates = Array.from(text.matchAll(/#EXT-X-PROGRAM-DATE-TIME:([^\n\r]+)/g))
        .map((match) => Date.parse(match[1]))
        .filter(Number.isFinite);
      if (programDates.length > 0) {
        const newest = Math.max(...programDates);
        if (Date.now() - newest > 30000) return false;
      }

      if (/#EXTINF:/i.test(text)) return true;

      if (/#EXT-X-STREAM-INF:/i.test(text)) {
        const variant = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .find((line) => line && !line.startsWith("#") && /\.m3u8([?#].*)?$/i.test(line));
        if (!variant) return false;
        try {
          return await hlsLooksLive(new URL(variant, url).toString());
        } catch {
          return false;
        }
      }

      return false;
    };

    const findLiveHlsSource = async (allowMediaPackage = false): Promise<string | null> => {
      const candidates = [
        config.relayHlsUrl,
        ...(allowMediaPackage ? [config.hlsUrl] : []),
      ].filter(Boolean);
      if (stickyLiveSource && candidates.includes(stickyLiveSource)) {
        return stickyLiveSource;
      }
      for (const candidate of candidates) {
        if (await hlsLooksLive(candidate)) {
          stickyLiveSource = candidate;
          return candidate;
        }
      }
      return null;
    };

    const setPlayerStatus = (next: Status) => {
      statusRef.current = next;
      setStatus(next);
    };

    const stopMode = () => {
      modeCleanup?.();
      modeCleanup = null;
      if (liveMonitorTimerRef.current !== null) {
        window.clearInterval(liveMonitorTimerRef.current);
        liveMonitorTimerRef.current = null;
      }
      if (liveRetryTimerRef.current !== null) {
        window.clearTimeout(liveRetryTimerRef.current);
        liveRetryTimerRef.current = null;
      }
      if (liveBootTimer !== null) {
        window.clearTimeout(liveBootTimer);
        liveBootTimer = null;
      }
      liveHls?.destroy();
      liveHls = null;
      playbackHls?.destroy();
      playbackHls = null;
      video.pause();
      video.removeAttribute("src");
      video.load();
    };

    const seekToLiveEdge = () => {
      try {
        const seekable = video.seekable;
        if (!seekable || seekable.length === 0) return;
        const liveEdge = seekable.end(seekable.length - 1);
        if (Number.isFinite(liveEdge) && liveEdge > 3) {
          video.currentTime = Math.max(0, liveEdge - 1.5);
        }
      } catch {
        // Some mobile browsers reject live edge seeks while metadata is settling.
      }
    };

    const broadcasterIsLive = async () => {
      try {
        const data = await fetchJsonWithTimeout<{ live?: boolean; active?: number; count?: number }>(config.liveStatusUrl, 3500);
        if (!data) return false;
        const live = typeof data.live === "boolean" ? data.live : (data.active ?? data.count ?? 0) > 0;
        if (live) {
          liveWentOfflineAt = 0;
        } else if (!liveWentOfflineAt) {
          liveWentOfflineAt = Date.now();
        }
        return live;
      } catch {
        if (!liveWentOfflineAt) {
          liveWentOfflineAt = Date.now();
        }
        return false;
      }
    };

    const isLiveArchiveItem = (item: PlaylistItem) => {
      const title = item.title.trim().toLowerCase();
      return title === "live" && /\/recordings\/.+\/archive\.mp4(\?|$)/.test(item.src);
    };

    const startLivePolling = () => {
      if (livePollTimer) return;
      livePollTimer = window.setInterval(() => {
        void broadcasterIsLive().then((isLive) => {
          if (disposed) return;
          if (isLive && statusRef.current !== "live") {
            void findLiveHlsSource(true).then((liveSource) => {
              if (!disposed && liveSource) {
                (config as { hlsUrl: string }).hlsUrl = liveSource;
                attachLive();
              }
            });
            return;
          }
          if (statusRef.current !== "live") {
            void findLiveHlsSource().then((liveSource) => {
              if (!disposed && liveSource && statusRef.current !== "live") {
                (config as { hlsUrl: string }).hlsUrl = liveSource;
                attachLive();
              }
            });
          }
        });
      }, LIVE_MONITOR_MS);
    };

    const stopLivePolling = () => {
      if (!livePollTimer) return;
      window.clearInterval(livePollTimer);
      livePollTimer = null;
    };

    const startLiveMonitor = () => {
      if (liveMonitorTimerRef.current !== null) return;
      liveMonitorTimerRef.current = window.setInterval(() => {
        if (disposed || statusRef.current !== "live") return;
        const currentTime = video.currentTime || 0;
        if (Math.abs(currentTime - lastLiveTime) > 0.25) {
          lastLiveTime = currentTime;
          lastLiveProgressAt = Date.now();
        } else if (!video.paused && Date.now() - lastLiveProgressAt > LIVE_STALL_GRACE_MS) {
          void broadcasterIsLive().then((isLive) => findLiveHlsSource(isLive)).then((liveSource) => {
            if (disposed || statusRef.current !== "live") return;
            if (liveSource) {
              (config as { hlsUrl: string }).hlsUrl = liveSource;
              lastLiveProgressAt = Date.now();
              seekToLiveEdge();
              return;
            }
            scheduleLiveFallback("network");
          });
          return;
        }
        void broadcasterIsLive().then((isLive) => {
          if (disposed) return;
          if (isLive) return;
          void findLiveHlsSource().then((liveSource) => {
            if (disposed || statusRef.current !== "live") return;
            if (liveSource) {
              (config as { hlsUrl: string }).hlsUrl = liveSource;
              return;
            }
            scheduleLiveFallback("network");
          });
        });
      }, LIVE_MONITOR_MS);
    };

    const scheduleLiveFallback = (reason: "network" | "error") => {
      if (disposed) return;
      if (liveRetryTimerRef.current !== null) {
        window.clearTimeout(liveRetryTimerRef.current);
      }
      setPlayerStatus("loading");
      setHeadline(reason === "network" ? "Checking stream..." : "Checking live feed...");
      liveRetryTimerRef.current = window.setTimeout(() => {
        liveRetryTimerRef.current = null;
        void broadcasterIsLive().then((isLive) => {
          if (disposed) return;
          if (isLive) {
            void findLiveHlsSource(true).then((liveSource) => {
              if (!disposed && liveSource) {
                (config as { hlsUrl: string }).hlsUrl = liveSource;
                attachLive();
              }
            });
            return;
          }
          void findLiveHlsSource().then((liveSource) => {
            if (disposed) return;
            if (liveSource) {
              (config as { hlsUrl: string }).hlsUrl = liveSource;
              attachLive();
              return;
            }
          const stillWithinLiveGrace = liveWentOfflineAt !== 0 && Date.now() - liveWentOfflineAt < LIVE_OFFLINE_GRACE_MS;
          if (stillWithinLiveGrace) {
            scheduleLiveFallback(reason);
            return;
          }
          const safePlaylistItems = playlistManifest.items.filter((item) => !isLiveArchiveItem(item));
          if (safePlaylistItems.length > 0) {
            void startPlaylist();
          } else {
            setPlayerStatus("offline");
            setHeadline("No live stream right now");
            startLivePolling();
          }
          });
        });
      }, LIVE_BOOT_RETRY_MS);
    };

    const attachLive = () => {
      stopLivePolling();
      const nextLiveSource = config.hlsUrl;
      if (currentLiveSource === nextLiveSource && statusRef.current === "live") {
        liveHls?.startLoad(-1);
        seekToLiveEdge();
        void video.play().catch(() => {});
        return;
      }
      stopMode();
      currentLiveSource = nextLiveSource;
      setPlayerStatus("loading");
      setHeadline("Checking live feed...");

      const onPlaying = () => {
        if (disposed) return;
        if (liveBootTimer !== null) {
          window.clearTimeout(liveBootTimer);
          liveBootTimer = null;
        }
        lastLiveTime = video.currentTime || 0;
        lastLiveProgressAt = Date.now();
        setPlayerStatus("live");
        setHeadline("Live now");
        startLiveMonitor();
      };
      const onTimeUpdate = () => {
        lastLiveTime = video.currentTime || 0;
        lastLiveProgressAt = Date.now();
      };
      const onError = () => {
        if (disposed) return;
        recoverLivePlayback("error");
      };

      video.addEventListener("playing", onPlaying);
      video.addEventListener("timeupdate", onTimeUpdate);
      video.addEventListener("error", onError);
      video.addEventListener("loadedmetadata", seekToLiveEdge);
      video.addEventListener("canplay", seekToLiveEdge, { once: true });

      modeCleanup = () => {
        video.removeEventListener("playing", onPlaying);
        video.removeEventListener("timeupdate", onTimeUpdate);
        video.removeEventListener("error", onError);
        video.removeEventListener("loadedmetadata", seekToLiveEdge);
        video.removeEventListener("canplay", seekToLiveEdge);
      };

      const recoverLivePlayback = (reason: "network" | "error") => {
        if (disposed) return;
        const stalledFor = Date.now() - lastLiveProgressAt;
        if (stalledFor < LIVE_STALL_GRACE_MS) {
          try {
            liveHls?.startLoad(-1);
            if (reason === "error") liveHls?.recoverMediaError();
            seekToLiveEdge();
            void video.play().catch(() => {});
          } catch {
            // If hls.js refuses recovery, the delayed fallback below will take over.
          }
          if (liveRetryTimerRef.current === null) {
            liveRetryTimerRef.current = window.setTimeout(() => {
              liveRetryTimerRef.current = null;
              if (!disposed && statusRef.current === "live" && Date.now() - lastLiveProgressAt > LIVE_STALL_GRACE_MS) {
                scheduleLiveFallback(reason);
              }
            }, Math.max(1000, LIVE_STALL_GRACE_MS - stalledFor));
          }
          return;
        }
        scheduleLiveFallback(reason);
      };

      try {
        video.muted = true;
        video.volume = 1;
        if (liveBootTimer !== null) {
          window.clearTimeout(liveBootTimer);
          liveBootTimer = null;
        }
        liveBootTimer = window.setTimeout(() => {
          liveBootTimer = null;
          if (disposed || statusRef.current !== "loading") return;
          const safePlaylistItems = playlistManifest.items.filter((item) => !isLiveArchiveItem(item));
          if (safePlaylistItems.length > 0) {
            void startPlaylist();
          } else {
            scheduleLiveFallback("network");
          }
        }, LIVE_BOOT_TIMEOUT_MS);
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = config.hlsUrl;
          void video.play().catch(() => {});
        } else if (Hls.isSupported()) {
          liveHls = new Hls({
            lowLatencyMode: false,
            capLevelToPlayerSize: true,
            startLevel: -1,
            liveSyncDurationCount: 5,
            liveMaxLatencyDurationCount: 10,
            maxBufferLength: 30,
            backBufferLength: 0,
            maxLiveSyncPlaybackRate: 1.02,
            manifestLoadingMaxRetry: 20,
            manifestLoadingRetryDelay: 1000,
            levelLoadingMaxRetry: 20,
            levelLoadingRetryDelay: 1000,
            fragLoadingMaxRetry: 20,
            fragLoadingRetryDelay: 1000,
            abrEwmaFastLive: 3,
            abrEwmaSlowLive: 8,
            abrBandWidthFactor: 0.7,
            abrBandWidthUpFactor: 0.5,
          });
          liveHls.loadSource(config.hlsUrl);
          liveHls.attachMedia(video);
          liveHls.on(Hls.Events.MANIFEST_PARSED, () => {
            liveHls?.startLoad(-1);
            seekToLiveEdge();
          });
          liveHls.on(Hls.Events.ERROR, (_e, data) => {
            if (!data.fatal) return;
            recoverLivePlayback(data.type === "networkError" ? "network" : "error");
          });
        } else {
          onError();
        }
      } catch {
        onError();
      }
    };

    const startPlaylist = async () => {
      if (playlistAbort || disposed) return;
      stopMode();

      const manifest = playlistManifest.items.length > 0 ? playlistManifest : await loadPlaylist();
      if (playlistAbort || disposed) return;
      playlistManifest = {
        ...manifest,
        items: manifest.items.filter((item) => !isLiveArchiveItem(item)),
      };

      const items = playlistManifest.items;
      setPlaylistSize(items.length);
      if (items.length === 0) {
        setPlayerStatus("offline");
        setHeadline("No live stream right now");
        startLivePolling();
        return;
      }

      if (liveMonitorTimerRef.current !== null) {
        window.clearInterval(liveMonitorTimerRef.current);
        liveMonitorTimerRef.current = null;
      }
      startLivePolling();
      setPlayerStatus("playlist");
      setHeadline("Channel playlist");

      let index = 0;
      const onEnded = () => {
        if (playlistAbort || disposed) return;
        index = (index + 1) % items.length;
        void playItem(items[index]);
      };
      const onError = () => {
        if (playlistAbort || disposed) return;
        index = (index + 1) % items.length;
        void playItem(items[index]);
      };

      async function playItem(item: PlaylistItem) {
        if (playlistAbort || disposed) return;
        stopMode();
        setPlayerStatus("playlist");
        video.poster = item.poster ?? "";
        video.addEventListener("ended", onEnded);
        video.addEventListener("error", onError);
        modeCleanup = () => {
          video.removeEventListener("ended", onEnded);
          video.removeEventListener("error", onError);
        };

        video.muted = true;
        video.volume = 1;
        if (isHlsSource(item)) {
            if (video.canPlayType("application/vnd.apple.mpegurl")) {
              video.src = item.src;
              await video.play().catch(() => {
                if (item.fallbackSrc) void playFallback(item.fallbackSrc);
              });
              return;
            }
            if (Hls.isSupported()) {
              playbackHls = new Hls({
                lowLatencyMode: false,
                capLevelToPlayerSize: true,
                maxBufferLength: 8,
                backBufferLength: 0,
              });
              playbackHls.loadSource(item.src);
              playbackHls.attachMedia(video);
              playbackHls.on(Hls.Events.MANIFEST_PARSED, () => {
              void video.play().catch(() => {});
            });
            playbackHls.on(Hls.Events.ERROR, (_e, data) => {
              if (data.fatal) {
                if (item.fallbackSrc) {
                  void playFallback(item.fallbackSrc);
                  return;
                }
                onError();
              }
            });
            return;
          }
        }

        video.src = item.src;
        await video.play().catch(() => {
          if (item.fallbackSrc) void playFallback(item.fallbackSrc);
        });
      }

      async function playFallback(src: string) {
        if (playlistAbort || disposed) return;
        if (src === video.src) return;
        stopMode();
        video.poster = "";
        setPlayerStatus("playlist");
        video.addEventListener("ended", onEnded);
        video.addEventListener("error", onError);
        modeCleanup = () => {
          video.removeEventListener("ended", onEnded);
          video.removeEventListener("error", onError);
        };
        if (src.endsWith(".m3u8") && Hls.isSupported() && !video.canPlayType("application/vnd.apple.mpegurl")) {
          playbackHls = new Hls({
            lowLatencyMode: false,
            capLevelToPlayerSize: true,
            maxBufferLength: 8,
            backBufferLength: 0,
          });
          playbackHls.loadSource(src);
          playbackHls.attachMedia(video);
          playbackHls.on(Hls.Events.MANIFEST_PARSED, () => {
            void video.play().catch(() => {});
          });
          playbackHls.on(Hls.Events.ERROR, (_e, data) => {
            if (data.fatal) onError();
          });
          return;
        }
        video.muted = true;
        video.volume = 1;
        video.src = src;
        await video.play().catch(() => {});
      }

      await playItem(items[index]);
    };

    void loadPlaylist().then((manifest) => {
      if (disposed) return;
      playlistManifest = manifest;
      setPlaylistSize(manifest.items.length);
      if (manifest.items.filter((item) => !isLiveArchiveItem(item)).length > 0) {
        void startPlaylist();
      } else {
        setPlayerStatus("loading");
        setHeadline("Checking live feed...");
        startLivePolling();
      }

      void broadcasterIsLive().then((isLive) => {
        if (disposed || !isLive) return;
        void findLiveHlsSource(true).then((liveSource) => {
          if (disposed) return;
          if (liveSource) {
            (config as { hlsUrl: string }).hlsUrl = liveSource;
            attachLive();
            return;
          }
          scheduleLiveFallback("network");
        });
      });
    });

    return () => {
      disposed = true;
      playlistAbort = true;
      stopLivePolling();
      if (liveMonitorTimerRef.current !== null) {
        window.clearInterval(liveMonitorTimerRef.current);
        liveMonitorTimerRef.current = null;
      }
      if (liveBootTimer !== null) {
        window.clearTimeout(liveBootTimer);
        liveBootTimer = null;
      }
      stopMode();
    };
  }, []);

  return (
    <div className="viewer">
      <h1>Now Live</h1>
      <div className="player">
        <video ref={videoRef} controls autoPlay playsInline muted />
        {status !== "live" && status !== "playlist" && (
          <div className="player-overlay">
            {status === "loading" && <span>{headline}</span>}
            {status === "offline" && <span>{headline}</span>}
            {status === "error" && <span>{headline}</span>}
          </div>
        )}
      </div>
      {banner && (
        <div className="watch-banner" data-banner-id={banner.id || undefined}>
          {banner.clickUrl ? (
            <a
              href={banner.clickUrl}
              target={banner.newTab ? "_blank" : undefined}
              rel={banner.newTab ? "noopener sponsored" : "sponsored"}
            >
              <img src={banner.imageUrl} alt={banner.title} loading="lazy" />
            </a>
          ) : (
            <img src={banner.imageUrl} alt={banner.title} loading="lazy" />
          )}
        </div>
      )}
      {playlistSize > 0 && (
        <p className="muted small">Playlist ready · {playlistSize} videos queued</p>
      )}
    </div>
  );
}
